import { ts } from '@shared/lib/diagTs';
import { getRelayOwnership } from '@core/realtime/relayOwnership';
import { allowRithmicAutoResume, getRithmicRelay } from './rithmicRelay';
import { storeRithmicCreds } from './rithmicCreds';
import { rc } from './rithmicLog';

/**
 * THE user-initiated Rithmic connect sequence, extracted from the connect dialog so every connect
 * surface (the dialog, the Add-account dialog) runs the SAME safety steps (mirror of
 * `cqgConnectFlow.ts` — see its doc for why a bare `mutateAsync` is not enough):
 * re-arm auto-resume → stash creds (relay builds the RequestLogin from them at socket-open; the ONLY
 * place they go — never our BE) → relay hub up BEFORE the POST → the `{system}`-only POST.
 * `creds: null` skips the stash (refresh flows must never re-store over a stale-mounted form).
 * The POST rejection is rethrown for the caller's error UI.
 */
export async function runRithmicConnectSequence(args: {
  /** `location` (2026-07-29) is the chosen point of presence; omit for the core (Chicago) gateway. */
  creds: { system: string; username: string; password: string; location?: string } | null;
  /** `useRithmicConnect().mutateAsync` — passed in so this stays a plain, hook-free lib function. */
  mutate: () => Promise<unknown>;
}): Promise<void> {
  allowRithmicAutoResume();
  // D1 (2026-09-13): an explicit Connect is a CLAIM — the tab that currently owns the Rithmic connection
  // closes its socket and releases before this tab stores creds and POSTs.
  await getRelayOwnership('rithmic').claim();
  if (args.creds) {
    storeRithmicCreds(
      args.creds.system, args.creds.username.trim(), args.creds.password, args.creds.location ?? '');
  }
  try {
    await getRithmicRelay().start();
  } catch (err) {
    rc.error(ts(), '[rithmic-connect] relay.start() failed', err);
  }
  await args.mutate();
}
